import type { RoundTripDatabaseEntry } from '@shared/database-entry-roundtrip';
import type { VoiceBuilderState } from '@shared/voice-quote-merge';

export interface TranscriptionResult {
  success: boolean;
  transcription?: string;
  duration?: number;
  error?: string;
}

export type DatabaseEntry = RoundTripDatabaseEntry;

export interface AIProcessingResult {
  success: boolean;
  entries?: DatabaseEntry[];
  processingInfo?: {
    itemsProcessed: number;
    confidence: number;
  };
  duration?: number;
  error?: string;
}

export interface VoiceToDatabaseResult {
  success: boolean;
  transcription?: string;
  entries?: DatabaseEntry[];
  processingInfo?: {
    itemsProcessed: number;
    confidence: number;
  };
  duration?: {
    transcription?: number;
    aiProcessing?: number;
    total?: number;
  };
  error?: string;
  step?: string;
}

export interface VoiceDatabase {
  id: string;
  name: string;
  type: string;
  createdAt: string;
  updatedAt: string;
  entries: DatabaseEntry[];
  metadata?: {
    entryCount: number;
    [key: string]: any;
  };
}

export interface VoiceDatabaseSummary {
  id: string;
  name: string;
  type: string;
  createdAt: string;
  updatedAt: string;
  entryCount: number;
  metadata?: {
    [key: string]: any;
  };
}

export interface SaveDatabaseResult {
  success: boolean;
  databaseId?: string;
  name?: string;
  entryCount?: number;
  createdAt?: string;
  error?: string;
}

export interface LoadDatabasesResult {
  success: boolean;
  databases?: VoiceDatabaseSummary[];
  count?: number;
  error?: string;
}

export interface LoadDatabaseResult {
  success: boolean;
  database?: VoiceDatabase;
  error?: string;
}

export interface ServiceStatus {
  whisper: {
    configured: boolean;
    connected: boolean;
    status: 'ready' | 'error' | 'not-configured';
  };
  n8n: {
    configured: boolean;
    connected: boolean;
    status: 'ready' | 'error' | 'not-configured';
  };
  overall: 'ready' | 'partial' | 'error';
}



class VoiceAPIService {
  private baseUrl = import.meta.env.DEV ? 'http://localhost:3003/api' : '/api';

  // Transcribe audio file using Whisper API
  async transcribeAudio(audioFile: File): Promise<TranscriptionResult> {
    try {
      const formData = new FormData();
      formData.append('audio', audioFile);

      const response = await fetch(`${this.baseUrl}/transcribe`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || `Server error: ${response.status}`
        };
      }

      return {
        success: true,
        transcription: result.data.transcription,
        duration: result.data.duration
      };

    } catch (error) {
      console.error('Transcription API error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }



  // Process transcription with AI to generate database entries
  async processWithAI(
    transcription: string, 
    databaseName?: string, 
    context?: string
  ): Promise<AIProcessingResult> {
    try {
      const response = await fetch(`${this.baseUrl}/ai-populate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transcription,
          databaseName,
          context
        })
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || `Server error: ${response.status}`
        };
      }

      return {
        success: true,
        entries: result.data.entries,
        processingInfo: result.data.processingInfo,
        duration: result.data.duration
      };

    } catch (error) {
      console.error('AI processing API error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  // Combined: transcribe and process in one call
  async voiceToDatabase(
    audioFile: File, 
    databaseName?: string, 
    context?: string
  ): Promise<VoiceToDatabaseResult> {
    try {
      const formData = new FormData();
      formData.append('audio', audioFile);
      
      if (databaseName) {
        formData.append('databaseName', databaseName);
      }
      
      if (context) {
        formData.append('context', context);
      }

      const response = await fetch(`${this.baseUrl}/voice-to-database`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || `Server error: ${response.status}`,
          step: result.step,
          transcription: result.transcription,
          duration: result.duration
        };
      }

      return {
        success: true,
        transcription: result.data.transcription,
        entries: result.data.entries,
        processingInfo: result.data.processingInfo,
        duration: result.data.duration
      };

    } catch (error) {
      console.error('Voice to database API error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  // Process voice input (alias for voiceToDatabase for backward compatibility)
  async processVoiceInput(audioBlob: Blob): Promise<VoiceToDatabaseResult> {
    // Convert Blob to File if needed
    const audioFile = audioBlob instanceof File ? audioBlob : new File([audioBlob], 'voice-input.webm', { type: 'audio/webm' });
    
    return await this.voiceToDatabase(audioFile);
  }

  // Check service status with optimized caching and N8N test logic
  async getServiceStatus(): Promise<ServiceStatus | null> {
    try {
      // Try fast health endpoint first - now optimized for N8N test logic
      const healthResponse = await fetch(`${this.baseUrl}/voice-services/health`, {
        signal: AbortSignal.timeout(2000) // 2 second timeout
      });
      
      if (healthResponse.ok) {
        const healthResult = await healthResponse.json();
        if (healthResult.success && healthResult.data) {
          const data = healthResult.data;
          
          // Use detailed service information from optimized health check
          return {
            whisper: {
              configured: data.services?.whisper || false,
              connected: data.services?.whisper || false,
              status: data.services?.whisper ? 'ready' : 'not-configured'
            },
            n8n: {
              configured: data.services?.n8n || false,
              connected: data.services?.n8n || false,
              status: data.services?.n8n ? 'ready' : 'not-configured'
            },
            overall: data.healthy ? 'ready' : 'partial'
          };
        }
      }
      
      // Fallback to full status check with timeout
      const response = await fetch(`${this.baseUrl}/voice-services/status`, {
        signal: AbortSignal.timeout(8000) // 8 second timeout for full check
      });
      
      if (!response.ok) {
        throw new Error(`Status check failed: ${response.status}`);
      }

      const result = await response.json();
      return result.data;

    } catch (error) {
      console.error('Service status check error:', error);
      
      // Return fallback status that allows partial functionality
      return {
        whisper: {
          configured: false,
          connected: false,
          status: 'not-configured'
        },
        n8n: {
          configured: false,
          connected: false,
          status: 'not-configured'
        },
        overall: 'error'
      };
    }
  }

  // Test if services are ready
  async areServicesReady(): Promise<boolean> {
    const status = await this.getServiceStatus();
    
    // Consider services ready if overall status is 'ready' or 'partial'
    // Even with partial services, basic functionality should work
    if (!status) {
      return false;
    }
    
    // Allow usage even if only basic services are available
    return status.overall === 'ready' || status.overall === 'partial';
  }

  // Get human-readable status message
  async getStatusMessage(): Promise<string> {
    const status = await this.getServiceStatus();
    
    if (!status) {
      return 'Verbindung zum Server fehlgeschlagen - Server möglicherweise nicht erreichbar';
    }

    if (status.overall === 'ready') {
      return 'Alle Services bereit - Voice Database funktionsfähig';
    }

    if (status.overall === 'partial') {
      const workingServices: string[] = [];
      const failingServices: string[] = [];
      
      if (status.whisper.status === 'ready') {
        workingServices.push('Sprach-zu-Text');
      } else if (status.whisper.status === 'not-configured') {
        failingServices.push('OpenAI API-Schlüssel nicht konfiguriert');
      } else {
        failingServices.push('OpenAI API nicht erreichbar');
      }

      if (status.n8n.status === 'ready') {
        workingServices.push('KI-Verarbeitung');
      } else if (status.n8n.status === 'not-configured') {
        failingServices.push('N8N Webhook nicht konfiguriert (optional)');
      } else {
        failingServices.push('N8N Webhook nicht erreichbar (optional)');
      }

      if (workingServices.length > 0) {
        return `Services teilweise bereit: ${workingServices.join(', ')} verfügbar. ${failingServices.length > 0 ? 'Fehlende Services: ' + failingServices.join(', ') : ''}`;
      }
      
      return `Grundfunktionen verfügbar. Fehlende Services: ${failingServices.join(', ')}`;
    }

    const issues: string[] = [];
    
    if (status.whisper.status === 'not-configured') {
      issues.push('OpenAI API-Schlüssel nicht konfiguriert');
    } else if (status.whisper.status === 'error') {
      issues.push('OpenAI API nicht erreichbar');
    }

    if (status.n8n.status === 'not-configured') {
      issues.push('N8N Webhook nicht konfiguriert (optional für KI-Features)');
    } else if (status.n8n.status === 'error') {
      issues.push('N8N Webhook nicht erreichbar (optional)');
    }

    if (issues.length === 0) {
      return 'Services nicht verfügbar - unbekannter Fehler';
    }

    return `Konfiguration erforderlich: ${issues.join(', ')}. App funktioniert mit eingeschränkten Features.`;
  }

  // Save voice database
  async saveDatabase(
    databaseName: string,
    entries: DatabaseEntry[],
    metadata?: any
  ): Promise<SaveDatabaseResult> {
    try {
      const response = await fetch(`${this.baseUrl}/voice-database/save-database`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          databaseName,
          entries,
          metadata
        })
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || `Server error: ${response.status}`
        };
      }

      return {
        success: true,
        databaseId: result.data.databaseId,
        name: result.data.name,
        entryCount: result.data.entryCount,
        createdAt: result.data.createdAt
      };

    } catch (error) {
      console.error('Save database API error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  // Load list of voice databases
  async loadDatabases(): Promise<LoadDatabasesResult> {
    try {
      const response = await fetch(`${this.baseUrl}/voice-database/databases`);
      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || `Server error: ${response.status}`
        };
      }

      return {
        success: true,
        databases: result.data.databases,
        count: result.data.count
      };

    } catch (error) {
      console.error('Load databases API error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  // Load specific voice database
  async loadDatabase(databaseId: string): Promise<LoadDatabaseResult> {
    try {
      const response = await fetch(`${this.baseUrl}/voice-database/databases/${databaseId}`);
      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || `Server error: ${response.status}`
        };
      }

      return {
        success: true,
        database: result.data
      };

    } catch (error) {
      console.error('Load database API error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  // Load ANY existing database (Excel uploads, quote data, etc.) and convert to voice database format
  async loadAnyDatabase(): Promise<LoadDatabasesResult> {
    try {
      console.log('🔄 Loading databases from all sources...');
      
      // Get all available databases from different sources with timeout
      const [voiceDatabases, excelUploads, quoteData] = await Promise.allSettled([
        this.loadDatabases(),
        Promise.race([
          fetch(`${this.baseUrl}/uploads`).then(r => r.json()),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Excel uploads timeout')), 8000)) // Increased timeout
        ]),
        Promise.race([
          fetch(`${this.baseUrl}/quote-data`).then(r => r.json()),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Quote data timeout')), 5000))
        ])
      ]);

      const allDatabases: VoiceDatabaseSummary[] = [];

      // Add voice databases
      if (voiceDatabases.status === 'fulfilled' && voiceDatabases.value.success && voiceDatabases.value.databases) {
        allDatabases.push(...voiceDatabases.value.databases);
        console.log(`✅ Added ${voiceDatabases.value.databases.length} voice databases`);
      }

      // Add Excel uploads as potential databases with improved error handling
      if (excelUploads.status === 'fulfilled' && excelUploads.value.success && excelUploads.value.data?.files) {
        console.log(`🔄 Processing ${excelUploads.value.data.files.length} Excel files...`);
        
        // Process Excel files in smaller batches with better error handling
        const batchSize = 2; // Reduced batch size for better stability
        const files = excelUploads.value.data.files;
        
        for (let i = 0; i < files.length; i += batchSize) {
          const batch = files.slice(i, i + batchSize);
          
          const batchResults = await Promise.allSettled(
            batch.map(async (file: any) => {
              try {
                console.log(`📊 Getting info for Excel file: ${file.metadata.originalName}`);
                
                // Multiple timeout strategies for Excel info requests
                const infoResponse = await Promise.race([
                  fetch(`${this.baseUrl}/uploads/${file.id}/info`),
                  new Promise<Response>((_, reject) => 
                    setTimeout(() => reject(new Error('Info timeout (10s)')), 10000) // Increased timeout
                  )
                ]);
                
                if (!infoResponse.ok) {
                  console.warn(`⚠️ HTTP error for ${file.id}: ${infoResponse.status}`);
                  throw new Error(`HTTP ${infoResponse.status}`);
                }
                
                const infoResult = await Promise.race([
                  infoResponse.json(),
                  new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('JSON parse timeout')), 5000)
                  )
                ]);
                
                const entryCount = infoResult.success ? (infoResult.data?.rowCount || 0) : 0;
                
                console.log(`✅ Excel file ${file.id} processed: ${entryCount} entries`);
                
                return {
                  id: `excel-${file.id}`,
                  name: file.metadata.originalName,
                  type: 'excel-upload',
                  createdAt: file.metadata.uploadedAt,
                  updatedAt: file.metadata.uploadedAt,
                  entryCount: entryCount,
                  metadata: {
                    originalName: file.metadata.originalName,
                    size: file.metadata.size,
                    fileId: file.id,
                    rowCount: entryCount,
                    status: infoResult.success ? 'ready' : 'error'
                  }
                };
              } catch (error) {
                console.warn(`⚠️ Could not get info for Excel file ${file.id}:`, (error as Error).message);

                // Still add the file but with error status and fallback info
                return {
                  id: `excel-${file.id}`,
                  name: file.metadata.originalName,
                  type: 'excel-upload',
                  createdAt: file.metadata.uploadedAt,
                  updatedAt: file.metadata.uploadedAt,
                  entryCount: 0,
                  metadata: {
                    originalName: file.metadata.originalName,
                    size: file.metadata.size,
                    fileId: file.id,
                    error: ((error as Error).message || '').includes('timeout') 
                      ? 'Verarbeitung dauerte zu lange (Excel-Datei zu komplex)'
                      : 'Fehler beim Laden der Excel-Datei',
                    status: 'error'
                  }
                };
              }
            })
          );
          
          // Add all results (both successful and failed)
          batchResults.forEach(result => {
            if (result.status === 'fulfilled') {
              allDatabases.push(result.value);
            }
          });
          
          // Longer delay between batches to reduce server load
          if (i + batchSize < files.length) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }
        
        const successfulExcelFiles = allDatabases.filter(db => 
          db.type === 'excel-upload' && !db.metadata?.error
        ).length;
        const failedExcelFiles = allDatabases.filter(db => 
          db.type === 'excel-upload' && db.metadata?.error
        ).length;
        
        console.log(`✅ Excel processing complete: ${successfulExcelFiles} successful, ${failedExcelFiles} failed`);
      } else if (excelUploads.status === 'rejected') {
        console.warn('⚠️ Failed to load Excel uploads:', excelUploads.reason.message);
      }

      // Add quote data as potential databases
      if (quoteData.status === 'fulfilled' && quoteData.value.success && quoteData.value.data?.files) {
        const quoteDbs: VoiceDatabaseSummary[] = quoteData.value.data.files.map((file: any) => ({
          id: `quote-${file.slot}`,
          name: file.filename || `Angebot Slot ${file.slot}`,
          type: 'quote-data',
          createdAt: file.createdAt,
          updatedAt: file.updatedAt,
          entryCount: file.itemCount || 0,
          metadata: {
            slot: file.slot,
            filename: file.filename,
            status: 'ready'
          }
        }));
        allDatabases.push(...quoteDbs);
        console.log(`✅ Added ${quoteDbs.length} quote databases`);
      } else if (quoteData.status === 'rejected') {
        console.warn('⚠️ Failed to load quote data:', quoteData.reason.message);
      }

      // Sort by creation date (newest first) and then by status (ready files first)
      allDatabases.sort((a, b) => {
        // First sort by status (ready files first)
        const aStatus = a.metadata?.status || 'ready';
        const bStatus = b.metadata?.status || 'ready';
        if (aStatus !== bStatus) {
          return aStatus === 'ready' ? -1 : 1;
        }
        // Then by creation date (newest first)
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });

      console.log(`✅ Total databases loaded: ${allDatabases.length}`);
      return {
        success: true,
        databases: allDatabases,
        count: allDatabases.length
      };

    } catch (error) {
      console.error('❌ Load any database API error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  // Load and convert any database to voice database format
  async loadAndConvertDatabase(databaseId: string): Promise<LoadDatabaseResult> {
    try {
      const [type, ...idParts] = databaseId.split('-');
      const id = idParts.join('-'); // Rejoin in case the ID contains hyphens

      switch (type) {
        case 'voice':
          // Load voice database directly
          return await this.loadDatabase(databaseId);

        case 'excel':
          console.log(`🔄 Loading Excel database: ${id}`);
          
          // Load Excel file and convert to database format with improved error handling
          const excelInfoResponse = await Promise.race([
            fetch(`${this.baseUrl}/uploads/${id}/info`),
            new Promise<Response>((_, reject) => 
              setTimeout(() => reject(new Error('Excel info request timeout (15s)')), 15000)
            )
          ]);
          
          if (!excelInfoResponse.ok) {
            throw new Error(`Excel file info request failed: HTTP ${excelInfoResponse.status}`);
          }
          
          const excelInfoResult = await Promise.race([
            excelInfoResponse.json(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Excel info JSON parse timeout')), 5000)
            )
          ]);
          
          if (!excelInfoResult.success) {
            throw new Error(excelInfoResult.error || 'Excel file info not available');
          }

          console.log(`📊 Excel info loaded, getting data for ${excelInfoResult.data?.rowCount || 0} rows...`);

          // Get the actual Excel data with timeout
          const excelDataResponse = await Promise.race([
            fetch(`${this.baseUrl}/uploads/${id}/data`),
            new Promise<Response>((_, reject) => 
              setTimeout(() => reject(new Error('Excel data request timeout (20s)')), 20000)
            )
          ]);
          
          if (!excelDataResponse.ok) {
            throw new Error(`Excel file data request failed: HTTP ${excelDataResponse.status}`);
          }
          
          const excelDataResult = await Promise.race([
            excelDataResponse.json(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Excel data JSON parse timeout')), 10000)
            )
          ]);
          
          if (!excelDataResult.success) {
            throw new Error(excelDataResult.error || 'Excel file data not available');
          }

          // Convert Excel rows to database entries with better error handling
          const excelEntries: DatabaseEntry[] = [];
          
          if (excelDataResult.data && Array.isArray(excelDataResult.data)) {
            for (const row of excelDataResult.data) {
              try {
                const entry: DatabaseEntry = {
                  artikelId: row.artikelId || row['artikel-id'] || '',
                  gewerk: row.gewerk || row.category || '',
                  name: row.name || row.bezeichnung || '',
                  beschreibung: row.beschreibung || row.description || row.name || 'Importiert aus Excel',
                  einheit: row.einheit || row.unit || 'Stk',
                  preis: parseFloat(row.preis || row.price || '0') || 0,
                  mwst: parseInt(row.mwst || row.vat || '19') || 19
                };
                
                // Only add entries with valid descriptions
                if (entry.beschreibung && entry.beschreibung.trim() !== '') {
                  excelEntries.push(entry);
                }
              } catch (rowError) {
                console.warn('⚠️ Skipping invalid Excel row:', rowError);
                continue;
              }
            }
          }

          console.log(`✅ Excel database loaded: ${excelEntries.length} valid entries`);

          return {
            success: true,
            database: {
              id: id,
              name: excelInfoResult.data.filename || 'Excel Import',
              type: 'excel-upload',
              entries: excelEntries,
              createdAt: excelInfoResult.data.uploadedAt || new Date().toISOString(),
              updatedAt: excelInfoResult.data.uploadedAt || new Date().toISOString(),
              metadata: {
                entryCount: excelEntries.length,
                originalFilename: excelInfoResult.data.filename,
                fileSize: excelInfoResult.data.size,
                rowCount: excelEntries.length,
                originalRowCount: excelDataResult.data?.length || 0
              }
            }
          };

        case 'quote':
          // Load quote data and convert to database format
          const quoteResponse = await fetch(`${this.baseUrl}/quote-data/${id}`);
          const quoteResult = await quoteResponse.json();
          
          if (!quoteResponse.ok) {
            throw new Error('Quote data not found');
          }

          // Convert quote items to database entries
          const entries: DatabaseEntry[] = quoteResult.data.items?.map((item: any) => ({
            artikelId: item.artikelId,
            gewerk: item.gewerk,
            name: item.name,
            beschreibung: item.beschreibung,
            einheit: item.einheit,
            preis: item.preis,
            mwst: item.mwst || 19
          })) || [];

          return {
            success: true,
            database: {
              id: databaseId,
              name: `Angebot: ${quoteResult.data.filename || `Slot ${id}`}`,
              type: 'quote-converted',
              createdAt: quoteResult.data.createdAt,
              updatedAt: quoteResult.data.updatedAt,
              entries,
              metadata: {
                entryCount: entries.length,
                originalType: 'quote-data',
                slot: id
              }
            }
          };

        default:
          throw new Error(`Unknown database type: ${type}`);
      }

    } catch (error) {
      console.error('❌ Load and convert database error:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to load database';
      
      if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          errorMessage = 'Datenbank-Verarbeitung dauerte zu lange (Timeout). Versuchen Sie es mit einer kleineren Datei oder später erneut.';
        } else if (error.message.includes('HTTP 404')) {
          errorMessage = 'Datenbank nicht gefunden. Möglicherweise wurde sie gelöscht.';
        } else if (error.message.includes('HTTP 500')) {
          errorMessage = 'Server-Fehler beim Laden der Datenbank. Versuchen Sie es später erneut.';
        } else if (error.message.includes('Excel')) {
          errorMessage = `Excel-Datei konnte nicht verarbeitet werden: ${error.message}`;
        } else {
          errorMessage = error.message;
        }
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Clear cache for specific Excel file (for retry functionality)
  async clearExcelFileCache(fileId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/uploads/${fileId}/cache`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        console.log(`✅ Cache cleared for Excel file: ${fileId}`);
        return true;
      } else {
        console.warn(`⚠️ Failed to clear cache for Excel file: ${fileId}`);
        return false;
      }
    } catch (error) {
      console.error('❌ Clear Excel cache error:', error);
      return false;
    }
  }

  // Load and convert any database to voice database format with retry support
  async loadAndConvertDatabaseWithRetry(databaseId: string, clearCache: boolean = false): Promise<LoadDatabaseResult> {
    try {
      // If it's an Excel file and clearCache is requested, clear the cache first
      if (clearCache && databaseId.startsWith('excel-')) {
        const [, ...idParts] = databaseId.split('-');
        const fileId = idParts.join('-');
        await this.clearExcelFileCache(fileId);
        
        // Small delay to ensure cache is cleared
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      return await this.loadAndConvertDatabase(databaseId);
    } catch (error) {
      console.error('❌ Load and convert database with retry error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load database'
      };
    }
  }

  // Save voice items as quote (with quantities)
  async saveVoiceQuote(
    header: any,
    items: any[],
    positions: any[],
    gewerkOrder: string[],
    options?: {
      filename?: string;
      targetSlot?: number;
      mergeStrategy?: 'replace' | 'append' | 'merge';
      metadata?: {
        folderId?: string | null;
        order?: number;
        voiceBuilderState?: VoiceBuilderState | null;
      };
    }
  ): Promise<{
    success: boolean;
    data?: {
      quoteData: any;
      slot: number;
      itemCount: number;
      mergeResult?: any;
    };
    error?: string;
  }> {
    try {
      console.log(`Saving voice quote: ${items.length} items, strategy: ${options?.mergeStrategy || 'replace'}`);
      
      const response = await fetch(`${this.baseUrl}/voice-database/save-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          header,
          items,
          positions,
          gewerkOrder,
          filename: options?.filename,
          targetSlot: options?.targetSlot,
          mergeStrategy: options?.mergeStrategy || 'replace',
          metadata: options?.metadata
        }),
      });

      // Parse response (handle both success and error cases)
      let result;
      const responseText = await response.text();
      
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', {
          status: response.status,
          statusText: response.statusText,
          responsePreview: responseText.substring(0, 500)
        });
        throw new Error(`Invalid JSON response from server (${response.status}): ${responseText.substring(0, 100)}`);
      }

      // Check if response indicates error
      if (!response.ok) {
        console.error('Save voice quote HTTP error:', response.status, result);
        throw new Error(result.error || `Server error ${response.status}`);
      }

      console.log('Save voice quote result:', result);

      if (!result.success) {
        throw new Error(result.error || 'Failed to save voice quote');
      }

      return result;
    } catch (error) {
      console.error('Save voice quote error:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const voiceAPI = new VoiceAPIService();
